import Decimal from 'decimal.js'
import { currencyFractionDigits } from '@shared/lib/format/currency'

const MoneyDecimal = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP })

export type DisplayMoney = { amount: string; currency: string }

function decimal(value: DisplayMoney): Decimal | null {
  try {
    const amount = new MoneyDecimal(value.amount)
    return amount.isFinite() ? amount : null
  } catch {
    return null
  }
}

/** Presentation never assigns a residual to another monetary record. */
export function roundMoneyForDisplay<T extends DisplayMoney>(value: T): T {
  const amount = decimal(value)
  return {
    ...value,
    amount: amount
      ? amount.toFixed(currencyFractionDigits(value.currency), Decimal.ROUND_HALF_UP)
      : value.amount,
  }
}

export function sumMoneyParts(
  values: readonly DisplayMoney[],
  currency: string,
): DisplayMoney | null {
  let sum = new MoneyDecimal(0)
  for (const value of values) {
    const amount = decimal(value)
    if (value.currency !== currency || !amount) return null
    sum = sum.plus(amount)
  }
  return { amount: sum.toFixed(), currency }
}

export function negateMoney<T extends DisplayMoney>(value: T): T {
  const amount = decimal(value)
  return { ...value, amount: amount ? amount.negated().toFixed() : value.amount }
}

export type MoneyReconciliationResult =
  | { kind: 'balanced' }
  | { kind: 'rounding'; difference: DisplayMoney }
  | { kind: 'mismatch' }

/** A rounding explanation is valid only when the original parts reconcile exactly. */
export function reconcileMoneyParts(
  total: DisplayMoney,
  parts: readonly DisplayMoney[],
): MoneyReconciliationResult {
  const exactTotal = decimal(total)
  const exactSum = sumMoneyParts(parts, total.currency)
  if (!exactTotal || !exactSum || !exactTotal.eq(exactSum.amount)) {
    return { kind: 'mismatch' }
  }
  const visibleSum = sumMoneyParts(parts.map(roundMoneyForDisplay), total.currency)!
  const difference = new MoneyDecimal(roundMoneyForDisplay(total).amount).minus(visibleSum.amount)
  return difference.isZero()
    ? { kind: 'balanced' }
    : { kind: 'rounding', difference: { amount: difference.toFixed(), currency: total.currency } }
}

export function moneyMovementTotals(
  movements: readonly { direction: string; amount: DisplayMoney }[],
) {
  const groups = new Map<
    string,
    { direction: 'IN' | 'OUT' | 'HELD'; currency: string; parts: DisplayMoney[] }
  >()
  for (const movement of movements) {
    const direction = movement.direction
    if (direction !== 'IN' && direction !== 'OUT' && direction !== 'HELD') continue
    const currency = movement.amount.currency
    const key = `${currency}:${direction}`
    const group = groups.get(key) ?? { direction, currency, parts: [] }
    group.parts.push(movement.amount)
    groups.set(key, group)
  }
  return [...groups.values()].flatMap((group) => {
    const total = sumMoneyParts(group.parts, group.currency)
    return total ? [{ ...group, total }] : []
  })
}
